import { lazy, Suspense, useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "./lib/api";
import { FavoritesProvider } from "./lib/favorites";
import { I18nProvider, useI18n, type Locale } from "./lib/i18n";
import { SessionProvider, useSession } from "./lib/session";
import { IconChat, IconGlobe, IconHeart, IconHome, IconSearch, IconTag, IconUser } from "./ui/icons";
import { Loading, ToastProvider } from "./ui/kit";
import { Browse, Home } from "./pages/Browse";
import { ListingPage } from "./pages/Listing";
import { SignIn } from "./pages/SignIn";
import { SellHub, SellNew } from "./pages/SellHub";
import { SellFlow } from "./pages/SellFlow";
import { ManageListing } from "./pages/ManageListing";
import { Inbox, Thread } from "./pages/Inbox";
import { Receipt, VerifyReceipt } from "./pages/Receipt";
import { Saved } from "./pages/Saved";
import { Account, Notifications } from "./pages/Account";
import { Welcome } from "./pages/Welcome";
import { SellerPage } from "./pages/Seller";
import { ReportProblem, SupportList, SupportThread } from "./pages/Support";
import { AboutPage, ContactPage, Footer, HelpPage, PrivacyPage, SafetyPage, TermsPage } from "./pages/info/Info";

const Admin = lazy(() => import("./pages/admin/Admin"));

export function App() {
  return (
    <SessionProvider>
      <LocaleBridge />
    </SessionProvider>
  );
}

/** Keeps the signed-in user's saved language and the app's language in sync. */
function LocaleBridge() {
  const { me } = useSession();
  const onChange = useCallback(
    (l: Locale) => {
      if (me) api("/api/me", { method: "PATCH", body: { locale: l } }).catch(() => {});
    },
    [me],
  );
  return (
    <I18nProvider onChange={onChange}>
      <FavoritesProvider>
        <ToastProvider>
          <Shell />
        </ToastProvider>
      </FavoritesProvider>
    </I18nProvider>
  );
}

/** Search is in the header on every page; on the browse page it keeps the brand, storage and sort already chosen. */
function HeaderSearch() {
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const onBrowse = loc.pathname === "/";
  const [q, setQ] = useState("");
  useEffect(() => setQ(onBrowse ? (params.get("q") ?? "") : ""), [params, onBrowse]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (onBrowse) for (const k of ["brand", "storage", "sort"]) if (params.get(k)) next.set(k, params.get(k)!);
    if (q.trim()) next.set("q", q.trim());
    nav(`/?${next}`);
  };
  return (
    <form role="search" className="header-search" onSubmit={submit}>
      <label htmlFor="site-q" className="sr-only">{t("browse.searchLabel")}</label>
      <input id="site-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("browse.searchPlaceholder")} enterKeyHint="search" autoComplete="off" dir={q ? "auto" : undefined} />
      <button className="go" type="submit" aria-label={t("browse.search")}><IconSearch size={20} /></button>
    </form>
  );
}

function RedirectWithId({ to }: { to: string }) {
  const { id } = useParams();
  return <Navigate to={to.replace(":id", id ?? "")} replace />;
}

function Shell() {
  const { t, locale, setLocale } = useI18n();
  const { me } = useSession();
  const loc = useLocation();
  const inAdmin = loc.pathname.startsWith("/admin");
  // Screens with their own pinned bar at the bottom (chat composer, sell steps, payment) skip the footer.
  const showFooter = !inAdmin && !/^\/(inbox\/|sell\/|checkout\/|signin)/.test(loc.pathname);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    // The phone header slides away while reading down and comes back on any scroll up.
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      if (y < 120 || y < last - 4) setHidden(false);
      else if (y > last + 4) setHidden(true);
      last = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const tabs = [
    { to: "/", label: t("nav.browse"), icon: <IconHome />, end: true },
    { to: "/sell", label: t("nav.sell"), icon: <IconTag /> },
    { to: "/inbox", label: t("nav.deals"), icon: <IconChat />, badge: me?.unreadDeals },
    { to: "/saved", label: t("nav.saved"), icon: <IconHeart /> },
    { to: "/account", label: t("nav.account"), icon: <IconUser />, badge: me?.unreadNotifications },
  ];
  return (
    <div className="app">
      <a href="#main" className="skip-link">{t("a11y.skip")}</a>
      {!inAdmin && (
      <header className={`topbar${scrolled ? " scrolled" : ""}${hidden ? " hidden" : ""}`} onFocusCapture={() => setHidden(false)}>
        <div className="topbar-inner">
          <Link to="/" className="wordmark" aria-label="Walashy">Walashy</Link>
          <HeaderSearch />
          <div className="top-actions">
            <nav className="desktop-nav" aria-label={t("nav.browse")}>
              {tabs.slice(1, 4).map((tab) => (
                <NavLink key={tab.to} to={tab.to} end={tab.end}>
                  {tab.label}
                  {!!tab.badge && <span className="badge-dot">{tab.badge}</span>}
                </NavLink>
              ))}
            </nav>
            {/* The language switch is an icon; its name says which language it switches to. */}
            <button className="icon-btn" onClick={() => setLocale(locale === "ar" ? "en" : "ar")} lang={locale === "ar" ? "en" : "ar"} aria-label={`${t("lang.label")}: ${t("lang.switch")}`} title={t("lang.switch")}>
              <IconGlobe />
            </button>
            {/* The account sits apart from the page links, as its own outlined button. */}
            <NavLink to="/account" className="account-btn">
              <IconUser size={18} />
              <span>{t("nav.account")}</span>
              {!!me?.unreadNotifications && <span className="badge-dot">{me.unreadNotifications}</span>}
            </NavLink>
          </div>
        </div>
      </header>
      )}

      <main id="main" tabIndex={-1} className={inAdmin ? "page admin-page-root" : "page"}>
        <Suspense fallback={<Loading />}>
          <div key={loc.pathname} className="route-in">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/l/:id" element={<ListingPage />} />
            <Route path="/s/:id" element={<SellerPage />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/sell" element={<SellHub />} />
            <Route path="/sell/new" element={<SellNew />} />
            <Route path="/sell/:id" element={<SellFlow />} />
            <Route path="/sell/:id/manage" element={<ManageListing />} />
            <Route path="/sell/:id/offers" element={<RedirectWithId to="/sell/:id/manage" />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/inbox/:id" element={<Thread />} />
            <Route path="/inbox/:id/receipt" element={<Receipt />} />
            <Route path="/r/:code" element={<VerifyReceipt />} />
            <Route path="/saved" element={<Saved />} />
            <Route path="/account" element={<Account />} />
            {/* Old addresses from before Walashy went free and direct. */}
            <Route path="/favorites" element={<Navigate to="/saved" replace />} />
            <Route path="/offers" element={<Navigate to="/saved" replace />} />
            <Route path="/orders/*" element={<Navigate to="/inbox" replace />} />
            <Route path="/cart" element={<Navigate to="/inbox" replace />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/inbox/:id/problem" element={<ReportProblem />} />
            <Route path="/support" element={<SupportList />} />
            <Route path="/support/:id" element={<SupportThread />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/returns" element={<Navigate to="/safety" replace />} />
            <Route path="/admin/*" element={<Admin />} />
            <Route path="*" element={<Browse />} />
          </Routes>
          </div>
        </Suspense>
        {showFooter && <Footer />}
      </main>

      {!inAdmin && (
      <nav className="tabbar" aria-label="Main">
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end}>
            <span className="tab-icon">{tab.icon}</span>
            <span>{tab.label}</span>
            {!!tab.badge && <span className="badge-dot">{tab.badge}</span>}
          </NavLink>
        ))}
      </nav>
      )}
    </div>
  );
}
